/**
 * Subscription Welcome Page
 * Displays a welcome message after Mercado Pago subscription checkout
 * Uses the app's global theme system
 */
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  CreditCard,
  Home,
  Loader2,
  LogIn,
  Receipt,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import {
  formatAmount,
  formatFrequency,
  getStatusLabel,
  useSubscriptionDetails,
} from "@/hooks/use-subscription-details";
import { authClient } from "@/lib/auth-client";

/**
 * Loading state component
 */
function LoadingSkeleton({ t }: { t: (key: string) => string }) {
  return (
    <div className="container mx-auto flex min-h-[80vh] max-w-2xl items-center justify-center px-4 py-16">
      <Card className="w-full">
        <CardContent className="flex flex-col items-center gap-4 py-12">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t("billing.subscriptionWelcome.loading")}</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Error display component
 */
function ErrorDisplay({
  error,
  onRetry,
  onGoHome,
  t,
}: {
  error: Error;
  onRetry: () => void;
  onGoHome: () => void;
  t: (key: string) => string;
}) {
  const errorMessages: Record<string, { title: string; description: string }> = {
    SUBSCRIPTION_NOT_FOUND: {
      title: t("billing.subscriptionWelcome.errors.notFound.title"),
      description: t("billing.subscriptionWelcome.errors.notFound.description"),
    },
    SUBSCRIPTION_ACCESS_DENIED: {
      title: t("billing.subscriptionWelcome.errors.accessDenied.title"),
      description: t("billing.subscriptionWelcome.errors.accessDenied.description"),
    },
    SUBSCRIPTION_ID_REQUIRED: {
      title: t("billing.subscriptionWelcome.errors.incompleteLink.title"),
      description: t("billing.subscriptionWelcome.errors.incompleteLink.description"),
    },
    SUBSCRIPTION_FETCH_ERROR: {
      title: t("billing.subscriptionWelcome.errors.connectionError.title"),
      description: t("billing.subscriptionWelcome.errors.connectionError.description"),
    },
  };

  const errorInfo = errorMessages[error.message] || {
    title: t("billing.subscriptionWelcome.errors.unexpected.title"),
    description: t("billing.subscriptionWelcome.errors.unexpected.description"),
  };

  return (
    <div className="container mx-auto flex min-h-[80vh] max-w-md items-center justify-center px-4 py-16">
      <Card className="w-full">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <CardTitle>{errorInfo.title}</CardTitle>
          <CardDescription>{errorInfo.description}</CardDescription>
        </CardHeader>
        <CardFooter className="flex flex-col gap-3">
          {error.message === "SUBSCRIPTION_FETCH_ERROR" && (
            <Button className="w-full gap-2" onClick={onRetry} variant="outline">
              <RefreshCw className="h-4 w-4" />
              {t("billing.subscriptionWelcome.retry")}
            </Button>
          )}
          <Button className="w-full" onClick={onGoHome}>
            {t("billing.subscriptionWelcome.goHome")}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Missing preapproval_id display
 */
function MissingIdDisplay({ onGoHome, t }: { onGoHome: () => void; t: (key: string) => string }) {
  return (
    <div className="container mx-auto flex min-h-[80vh] max-w-md items-center justify-center px-4 py-16">
      <Card className="w-full">
        <CardHeader className="text-center">
          <div className="mb-4 flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <AlertCircle className="h-8 w-8 text-muted-foreground" />
            </div>
          </div>
          <CardTitle>{t("billing.subscriptionWelcome.incompleteLinkPage.title")}</CardTitle>
          <CardDescription>
            {t("billing.subscriptionWelcome.incompleteLinkPage.description")}
          </CardDescription>
        </CardHeader>
        <CardFooter>
          <Button className="w-full" onClick={onGoHome}>
            {t("billing.subscriptionWelcome.goHome")}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

/**
 * Main subscription welcome page component
 */
export default function SubscriptionWelcome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Get query parameters
  const preapprovalId = searchParams.get("preapproval_id");

  // Auth state
  const { data: session } = authClient.useSession();
  const isAuthenticated = !!session?.user;
  const userName = session?.user?.name || undefined;

  // Fetch subscription details
  const { data: subscription, isLoading, error, refetch } = useSubscriptionDetails(preapprovalId);

  // Navigation handlers
  const handleNavigateToDashboard = () => navigate("/dashboard");
  const handleNavigateToBilling = () => navigate("/billing");
  const handleNavigateToLogin = () => navigate("/auth/restaurant/sign-in");

  // Handle missing preapproval_id
  if (!preapprovalId) {
    return <MissingIdDisplay onGoHome={handleNavigateToDashboard} t={t} />;
  }

  // Handle loading state
  if (isLoading) {
    return <LoadingSkeleton t={t} />;
  }

  // Handle error state
  if (error) {
    return (
      <ErrorDisplay
        error={error}
        onGoHome={handleNavigateToDashboard}
        onRetry={() => refetch()}
        t={t}
      />
    );
  }

  // Handle no data (shouldn't happen if no error)
  if (!subscription) {
    return <LoadingSkeleton t={t} />;
  }

  const statusInfo = getStatusLabel(subscription.status);
  const formattedPrice = formatAmount(subscription.price.amount, subscription.price.currency);
  const formattedFrequency = formatFrequency(
    subscription.price.frequency,
    subscription.price.frequencyType,
  );

  return (
    <div className="container mx-auto max-w-2xl px-4 py-16">
      <div className="space-y-6">
        {/* Success Header */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="py-8 text-center">
            <div className="mb-4 flex justify-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                <CheckCircle className="h-8 w-8 text-primary" />
              </div>
            </div>

            <h1 className="mb-2 font-bold text-2xl">
              {isAuthenticated && userName
                ? t("billing.subscriptionWelcome.welcome", { name: userName.split(" ")[0] })
                : t("billing.subscriptionWelcome.welcomeGuest")}
            </h1>

            <p className="mb-4 text-muted-foreground">
              {t("billing.subscriptionWelcome.subscriptionActive")}
            </p>

            <Badge className="gap-1">
              <Sparkles className="h-3 w-3" />
              {statusInfo.label}
            </Badge>
          </CardContent>
        </Card>

        {/* Plan Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5" />
              {t("billing.subscriptionWelcome.planDetails")}
            </CardTitle>
            <CardDescription>{t("billing.subscriptionWelcome.planInfo")}</CardDescription>
          </CardHeader>

          <CardContent className="space-y-4">
            {/* Plan name and price */}
            <div className="rounded-lg bg-muted/50 p-4 text-center">
              <p className="mb-1 text-muted-foreground text-sm">
                {t("billing.subscriptionWelcome.yourPlan")}
              </p>
              <h2 className="mb-2 font-semibold text-xl">{subscription.plan.name}</h2>
              <div className="flex items-baseline justify-center gap-1">
                <span className="font-bold text-3xl text-primary">{formattedPrice}</span>
                <span className="text-muted-foreground">/ {formattedFrequency}</span>
              </div>
            </div>

            <Separator />

            {/* Info grid */}
            <div className="grid gap-4 sm:grid-cols-2">
              {subscription.dates.nextPayment && (
                <div className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                    <Calendar className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">
                      {t("billing.subscriptionWelcome.nextPayment")}
                    </p>
                    <p className="font-medium">
                      {subscription.dates.nextPayment.toLocaleDateString("es-AR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                      })}
                    </p>
                  </div>
                </div>
              )}

              {subscription.paymentMethod && (
                <div className="flex items-center gap-3 rounded-lg border p-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <div>
                    <p className="text-muted-foreground text-xs">
                      {t("billing.subscriptionWelcome.paymentMethod")}
                    </p>
                    <p className="font-medium capitalize">
                      {subscription.paymentMethod.replace(/_/g, " ")}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>

          <CardFooter className="flex flex-col gap-3 sm:flex-row">
            {isAuthenticated ? (
              <>
                <Button
                  className="w-full gap-2 sm:flex-1"
                  onClick={handleNavigateToDashboard}
                  size="lg"
                >
                  <Home className="h-4 w-4" />
                  {t("billing.subscriptionWelcome.goToDashboard")}
                </Button>
                <Button
                  className="w-full gap-2 sm:flex-1"
                  onClick={handleNavigateToBilling}
                  size="lg"
                  variant="outline"
                >
                  <Receipt className="h-4 w-4" />
                  {t("billing.subscriptionWelcome.manageSubscription")}
                </Button>
              </>
            ) : (
              <>
                <Button
                  className="w-full gap-2 sm:flex-1"
                  onClick={handleNavigateToLogin}
                  size="lg"
                >
                  <LogIn className="h-4 w-4" />
                  {t("billing.subscriptionWelcome.signIn")}
                </Button>
                <Button
                  className="w-full gap-2 sm:flex-1"
                  onClick={handleNavigateToDashboard}
                  size="lg"
                  variant="outline"
                >
                  <Home className="h-4 w-4" />
                  {t("billing.subscriptionWelcome.goHome")}
                </Button>
              </>
            )}
          </CardFooter>
        </Card>

        {/* Reference ID */}
        <div className="text-center">
          <p className="font-mono text-muted-foreground text-xs">
            {t("billing.subscriptionWelcome.reference", { id: subscription.id })}
          </p>
        </div>
      </div>
    </div>
  );
}
